import { cloneDeep } from 'lodash'
import { LabsConstants } from '../../procedures/labs/store/labs.constants'
import { initialLabsState } from '../../procedures/labs/store/labs.reducer'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { initialVitalsState } from '../../vitals/store/vitals.reducers'
import { IPatientVitals } from '../../vitals/store/vitals.types'
import { isValidVitalData } from '../../nemsisData/utils/customElements.util'
import { IPulsaraVitalsState, IPulsaraLabsState } from '../store/pulsara.types'
import { getFormattedDate, getFormattedTimeWithSeconds } from '../../../utilities/dateTime.util'

// Vitals/Labs before this cutoff are marked PTA
export function getPtaCutoff(
  atPatientDate: string | null,
  atPatientTime: string | null,
  onSceneDate: string | null,
  onSceneTime: string | null
): Date | null {
  const atPatient = atPatientDate && atPatientTime ? new Date(`${atPatientDate} ${atPatientTime}`) : null
  const onScene = onSceneDate && onSceneTime ? new Date(`${onSceneDate} ${onSceneTime}`) : null
  if (!atPatient && !onScene) return null
  if (!atPatient) return onScene
  if (!onScene) return atPatient
  return atPatient > onScene ? atPatient : onScene
}

function isPriorToArrival(
  eventDate: string | null | undefined,
  eventTime: string | null | undefined,
  ptaCutoff: Date | null
): boolean {
  if (!ptaCutoff) return false
  if (!eventDate || !eventTime) return false
  const eventDateTime = new Date(`${eventDate} ${eventTime}`)
  return eventDateTime < ptaCutoff
}

export const formatVitals = (vitals: IPulsaraVitalsState[], ptaCutoff: Date | null) => {
  const initialVitalsClone = cloneDeep(initialVitalsState)
  return vitals.map((vital) => {
    const { dateTime, gcsTotal, vitalId, ...restVital } = vital
    const formattedDate = new Date(dateTime)
    const date = getFormattedDate(formattedDate)
    const time = getFormattedTimeWithSeconds(formattedDate)
    return {
      ...initialVitalsClone,
      ...restVital,
      date,
      time,
      pta: isPriorToArrival(date, time, ptaCutoff),
    }
  })
}

export const formatLabs = (labs: IPulsaraLabsState[], ptaCutoff: Date | null) => {
  const initialLabsClone = cloneDeep(initialLabsState)
  return labs.map((lab) => {
    const { dateTime, labId, ...restLab } = lab
    const formattedDate = new Date(dateTime)
    const date = getFormattedDate(formattedDate)
    const time = getFormattedTimeWithSeconds(formattedDate)
    return {
      ...initialLabsClone,
      nemsisCode: LabsConstants.LABS_NEMSIS_CODE,
      ...restLab,
      date,
      time,
      pta: isPriorToArrival(date, time, ptaCutoff),
    }
  })
}

// Same filter as timeline/saga/addLabsItem.saga, plus import-only fields not present on ILabsState
const LABS_EVENT_SUBHEADER_FILTER_PROPS = [
  'key',
  'date',
  'time',
  'comments',
  'type',
  'clientCorrelationId',
  'nemsisCode',
  'pta',
]

export const createTimelineItems = (list: any[], type: string, ptaCutoff: Date | null) => {
  const createSubHeaderForTimeline = (item: any) => {
    const labValues: string[] = []
    Object.keys(item).forEach((prop) => {
      if (LABS_EVENT_SUBHEADER_FILTER_PROPS.includes(prop)) return
      const value = item[prop as keyof typeof item]
      if (value && typeof value === 'string') labValues.push(`${prop}:${value}`)
    })
    const assessedByLabel = item.assessedBy?.name ?? ''
    const comments = item.comments ?? ''
    return `By ${assessedByLabel}, Lab values: ${labValues.join(', ')}, Comments: ${comments}.`
  }

  return list.map((item) => {
    const isPTA = isPriorToArrival(item.date, item.time, ptaCutoff)
    const isValidVital = type === 'Vitals' ? isValidVitalData(item as IPatientVitals) : false
    return {
      key: item.key,
      itemType: type === 'Vitals' ? 'Vitals' : 'Procedures',
      grid: true,
      labelHeader: item.time,
      subHeader: item.date,
      labelHeaderFlag: isPTA ? 'PTA' : '',
      paragraph: type === 'Vitals' ? (item as IPulsaraVitalsState).comments : '',
      eventSubHeader: type === 'Vitals' ? '' : createSubHeaderForTimeline(item),
      clientCorrelationId:
        type === 'Vitals'
          ? isValidVital
            ? `patient.patientVitals_${item.key}`
            : ''
          : `patient.labsStateList${item.key}`,
      rowActionType: type === 'Vitals' ? TimelineConstants.SET_VITAL_ITEM : TimelineConstants.SET_LABS_PROCEDURE_ITEM,
      defaultHref: type === 'Vitals' ? TabConstants.VITALS_TAB : TabConstants.LABS_PROCEDURE,
      date: new Date(`${item.date} ${item.time}`),
      event: type,
      ...(type === 'Vitals' &&
        (() => {
          const v = item as IPulsaraVitalsState
          return {
            keyValues: [
              { key: 'HR', value: v.heartRate || '', columnSize: '1' },
              { key: 'BP', value: `${v.systolicBP || ''}/${v.diastolicBP || ''}`, columnSize: '1' },
              { key: 'RR', value: v.respirationRate || '', columnSize: '1' },
              { key: 'SpO2', value: v.spo2 || '', columnSize: '1' },
              { key: 'Temp', value: v.temperature || '', columnSize: '1' },
            ],
          }
        })()),
    }
  })
}
